import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Since there are no enum definitions in OpenFigiConcern::Enums,
// we only filter by text and date fields.

export const filterOpenFigisSchema = z.object({
  figi_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  composite_figi_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  share_class_figi_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  isin_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  ticker_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  name_contains: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({ id: z.number(), name: z.string(), text: z.string() }),
      ])
      .optional(),
  ),
  last_updated_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_updated_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterOpenFigisSchema = z.infer<typeof filterOpenFigisSchema>;

export const fields: Field[] = [
  {
    name: "figi_contains",
    label: "FIGI",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "figi",
  },
  {
    name: "composite_figi_contains",
    label: "Composite FIGI",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "composite",
  },
  {
    name: "share_class_figi_contains",
    label: "Share Class FIGI",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "figi_share_class",
  },
  {
    name: "isin_contains",
    label: "ISIN",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "unique_id",
  },
  {
    name: "ticker_contains",
    label: "Ticker",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "ticker",
  },
  {
    name: "name_contains",
    label: "Name",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/open_figis",
    column_name: "name",
  },
  {
    name: "last_updated_at_from",
    label: "Last Updated From",
    type: "date",
  },
  {
    name: "last_updated_at_to",
    label: "Last Updated To",
    type: "date",
  },
  {
    name: "created_at_from",
    label: "Created From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created To",
    type: "date",
  },
];

export const client = "qtl - referential";

export function FilterOpenFigis() {
  return (
    <FilterTable
      client={client}
      schema={filterOpenFigisSchema}
      tableName="open_figis"
      customBuildPath={() => `${client}/open_figis`}
      fields={fields}
    />
  );
}
